// biome-ignore-all lint/suspicious/noArrayIndexKey: the multi-band review rows are positional and edited in place through controlled props (no stable id until saved); index keys are correct here.
// =============================================================================
// SchoolByodManager.jsx — admin school-level BYOD band manager (BYOD Part 7)
// -----------------------------------------------------------------------------
// Lives at the bottom of the "Edit School" modal (SchoolFormModal, mode === "edit"
// only — a brand-new school has no id yet). Unlike the responder ByodLookupPanel
// (one child + one covering band), this is a SCHOOL-LEVEL view of ALL active bands:
//
//   • lists every active school_byod band (the list IS the selector when >1);
//   • Edit / view opens the shared ByodBandEditor for one band → single-band save
//     (deactivate-and-insert), same EF path the responder uses;
//   • "Add band" opens a blank editor for a manual single band;
//   • "Look up whole school" runs the grounded MULTI-BAND lookup (decision A1) and
//     shows every returned band in its own editor → save the whole set at once
//     (save_bands, which reconciles overlaps across the set — decision B2).
//
// Reuses the shared grid + helpers from ByodBandEditor so the fields never drift
// from the responder panel. Data via src/data/schoolByod; saves via the byod-lookup
// EF wrappers in src/services/byodLookup (admin qualifies as an internal caller, so
// no RLS / EF-auth change — see docs/byod-lookup-plan.md §Part 7 Access/RLS).
// =============================================================================

import React from "react";
import { PHASE_PRIMARY, PHASE_SECONDARY } from "../../../supabase/functions/_shared/byod/prompt";
import { listActiveBandsForSchool } from "../../data/schoolByod";
import { lookupByodBands, saveByodBand, saveByodBands } from "../../services/byodLookup";
import {
  ByodBandEditor,
  bandDraftFromBand,
  bandDraftFromSuggestion,
  bandDraftToFields,
  bandLabel,
  emptyBandDraft,
  TEAL,
  validateBandDraft,
} from "../request/ByodBandEditor.jsx";

function statusText(status) {
  return status === "byod"
    ? "Student brings own device"
    : status === "school_supplied"
      ? "School supplies device"
      : "Unknown";
}

const BTN = {
  background: TEAL,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 7,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};
const BTN_LIGHT = {
  background: "#fff",
  color: "#00695C",
  border: "1px solid #4DB6AC",
  borderRadius: 7,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
const BTN_PLAIN = {
  background: "#fff",
  color: "#607D79",
  border: "1px solid #CFE0DE",
  borderRadius: 7,
  padding: "7px 14px",
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "inherit",
};

// school: the persisted school row being edited ({ id, name, suburb, state }).
export function SchoolByodManager({ school }) {
  const schoolId = school?.id;
  const [bands, setBands] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode] = React.useState("list"); // "list" | "editOne" | "reviewSet"
  const [oneDraft, setOneDraft] = React.useState(null); // single-band editor draft
  const [setDrafts, setSetDrafts] = React.useState([]); // multi-band review drafts
  const [lookupMeta, setLookupMeta] = React.useState(null); // { confidence, sources, parsed }
  const [looking, setLooking] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadBands = React.useCallback(
    async function () {
      if (!schoolId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const rows = await listActiveBandsForSchool(schoolId);
        setBands(rows);
      } catch (_e) {
        // A client/read error must not crash the Edit-School modal or leak an
        // unhandled rejection — show no bands and let the admin retry.
        setBands([]);
      } finally {
        setLoading(false);
      }
    },
    [schoolId]
  );

  React.useEffect(
    function () {
      loadBands();
    },
    [loadBands]
  );

  function backToList() {
    setMode("list");
    setOneDraft(null);
    setSetDrafts([]);
    setLookupMeta(null);
    setError("");
  }

  // ── single band (edit an existing one, or add one manually) ────────────────
  function openEditBand(band) {
    setError("");
    setOneDraft(bandDraftFromBand(band));
    setMode("editOne");
  }
  function openAddBand() {
    setError("");
    setOneDraft(emptyBandDraft());
    setMode("editOne");
  }
  function setOneField(key, value) {
    setOneDraft((prev) => Object.assign({}, prev, { [key]: value }));
  }
  async function saveOne() {
    if (!oneDraft) return;
    const v = validateBandDraft(oneDraft);
    if (!v.ok) {
      setError(v.error);
      return;
    }
    setError("");
    setSaving(true);
    const res = await saveByodBand({
      schoolId,
      fields: bandDraftToFields(oneDraft),
      yearFrom: v.from,
      yearTo: v.to,
      source: "responder",
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    backToList();
    await loadBands();
  }

  // ── whole-school (multi-band) lookup + save ────────────────────────────────
  // BYOD Part 10 (10a): a scoped multi-band lookup. `scope` = { yearFrom, yearTo } for a
  // phase (Primary 0-6 / Secondary 7-12), or null for the whole-school (0-12) fallback.
  async function runBandsLookup(scope) {
    setError("");
    setLooking(true);
    const r = await lookupByodBands({
      schoolName: school?.name || "",
      suburb: school?.suburb || "",
      state: school?.state || "",
      ...(scope ? { yearFrom: scope.yearFrom, yearTo: scope.yearTo } : {}),
    });
    setLooking(false);
    if (!r.ok) {
      setError(r.error || "The lookup didn't return a result. Try again.");
      return;
    }
    const suggestions = r.suggestions || [];
    if (suggestions.length === 0) {
      setError(
        r.parsed
          ? "The lookup didn't find any bands. Try again, or add a band manually."
          : r.finishReason === "MAX_TOKENS"
            ? "The AI reply was cut off before it finished. Try a single phase (Primary or Secondary) rather than the whole school, or try again."
            : "Couldn't read a clear answer from the lookup. Check the sources or add a band manually."
      );
      return;
    }
    setSetDrafts(suggestions.map((s) => bandDraftFromSuggestion(s, null)));
    setLookupMeta({ confidence: r.confidence, sources: r.sources || [], parsed: r.parsed });
    setMode("reviewSet");
  }
  // BYOD Part 10 (10a): the lookup button(s), chosen by school_type. Primary/Secondary
  // auto-scope; Combined/Special/unknown must choose a phase; the whole-school (0-12)
  // search is kept as a fallback in every case.
  function lookupButtons() {
    const t = (school?.school_type || "").trim();
    const verb = bands.length ? "Refresh" : "Look up";
    const primary = (
      <button
        type="button"
        key="primary"
        style={BTN}
        disabled={looking}
        onClick={() => runBandsLookup(PHASE_PRIMARY)}
      >
        {looking ? "Looking up…" : `${verb} Primary (Prep–6)`}
      </button>
    );
    const secondary = (
      <button
        type="button"
        key="secondary"
        style={BTN}
        disabled={looking}
        onClick={() => runBandsLookup(PHASE_SECONDARY)}
      >
        {looking ? "Looking up…" : `${verb} Secondary (Year 7–12)`}
      </button>
    );
    const whole = (
      <button
        type="button"
        key="whole"
        style={BTN_PLAIN}
        disabled={looking}
        onClick={() => runBandsLookup(null)}
      >
        {looking ? "Looking up…" : `${verb} whole school (0–12)`}
      </button>
    );
    if (t === "Primary") return [primary, whole];
    if (t === "Secondary") return [secondary, whole];
    return [primary, secondary, whole];
  }

  function setDraftField(index, key, value) {
    setSetDrafts((prev) =>
      prev.map((d, i) => (i === index ? Object.assign({}, d, { [key]: value }) : d))
    );
  }
  function removeDraft(index) {
    setSetDrafts((prev) => prev.filter((_, i) => i !== index));
  }
  async function saveSet() {
    if (!setDrafts.length) {
      setError("There are no bands to save.");
      return;
    }
    const bandsPayload = [];
    for (const d of setDrafts) {
      const v = validateBandDraft(d);
      if (!v.ok) {
        setError(v.error);
        return;
      }
      bandsPayload.push({ fields: bandDraftToFields(d), yearFrom: v.from, yearTo: v.to });
    }
    setError("");
    setSaving(true);
    const res = await saveByodBands({ schoolId, bands: bandsPayload, source: "responder" });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    backToList();
    await loadBands();
  }

  if (!schoolId) return null;

  const wrap = {
    marginTop: 16,
    paddingTop: 12,
    borderTop: "1px solid #E0E7E6",
  };
  const heading = {
    margin: "0 0 8px",
    fontSize: 14,
    fontWeight: 700,
    color: "#1A2E2B",
  };

  return (
    <div style={wrap}>
      <h3 style={heading}>BYOD Policy Bands</h3>

      {loading ? (
        <span style={{ fontSize: 12, color: "#607D79" }}>Loading bands…</span>
      ) : mode === "editOne" && oneDraft ? (
        // ── single-band editor ───────────────────────────────────────────────
        <div>
          <ByodBandEditor edited={oneDraft} setField={setOneField} />
          {error && <div style={{ marginTop: 8, fontSize: 12, color: "#C62828" }}>{error}</div>}
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button type="button" style={BTN} disabled={saving} onClick={saveOne}>
              {saving ? "Saving…" : "Save band"}
            </button>
            <button type="button" style={BTN_PLAIN} disabled={saving} onClick={backToList}>
              Cancel
            </button>
          </div>
        </div>
      ) : mode === "reviewSet" ? (
        // ── whole-school review (multiple bands) ─────────────────────────────
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span
              style={{
                background: "#FFF3E0",
                border: "1px solid #FFB300",
                borderRadius: 5,
                padding: "2px 8px",
                fontSize: 11,
                fontWeight: 700,
                color: "#7c4a00",
              }}
            >
              AI — verify
            </span>
            <span style={{ fontSize: 12, color: "#607D79" }}>
              Confidence: {lookupMeta?.confidence || "low"} · {setDrafts.length} band
              {setDrafts.length === 1 ? "" : "s"}
            </span>
          </div>
          {setDrafts.map((d, i) => (
            <div
              key={i}
              style={{
                marginBottom: 10,
                padding: "8px 10px",
                borderRadius: 8,
                background: "#F2FAF9",
                border: "1px solid #B2DFDB",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#00695C" }}>
                  Band {i + 1}
                </span>
                <button
                  type="button"
                  style={{ ...BTN_PLAIN, padding: "2px 8px", fontSize: 12 }}
                  onClick={() => removeDraft(i)}
                >
                  Remove
                </button>
              </div>
              <ByodBandEditor edited={d} setField={(k, v) => setDraftField(i, k, v)} />
            </div>
          ))}
          {lookupMeta?.sources && lookupMeta.sources.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#607D79" }}>
              Sources:{" "}
              {lookupMeta.sources.map((u, i) => (
                <a
                  key={u}
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: TEAL, marginRight: 8 }}
                >
                  [{i + 1}]
                </a>
              ))}
            </div>
          )}
          {error && <div style={{ marginTop: 8, fontSize: 12, color: "#C62828" }}>{error}</div>}
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button type="button" style={BTN} disabled={saving} onClick={saveSet}>
              {saving
                ? "Saving…"
                : `Save ${setDrafts.length} band${setDrafts.length === 1 ? "" : "s"}`}
            </button>
            <button type="button" style={BTN_PLAIN} disabled={saving} onClick={backToList}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // ── list (the selector) ──────────────────────────────────────────────
        <div>
          {bands.length === 0 ? (
            <div style={{ fontSize: 13, color: "#607D79", marginBottom: 10 }}>
              No BYOD bands on file for this school.
            </div>
          ) : (
            <div style={{ marginBottom: 10 }}>
              {bands.map((b) => (
                <button
                  type="button"
                  key={b.id}
                  onClick={() => openEditBand(b)}
                  style={{
                    display: "flex",
                    width: "100%",
                    textAlign: "left",
                    alignItems: "baseline",
                    gap: 10,
                    padding: "7px 10px",
                    marginBottom: 4,
                    borderRadius: 6,
                    border: "1px solid #CFE0DE",
                    background: "#F7FCFB",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#00695C", minWidth: 120 }}>
                    {bandLabel(b.year_from, b.year_to)}
                  </span>
                  <span style={{ fontSize: 13, color: "#1A2E2B", flex: 1 }}>
                    {statusText(b.byod_status)}
                    {b.byod_device ? ` · ${b.byod_device}` : ""}
                  </span>
                  <span style={{ fontSize: 11, color: "#90A4A0" }}>
                    {b.byod_checked_at ? `checked ${String(b.byod_checked_at).slice(0, 10)}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
          {error && <div style={{ marginBottom: 8, fontSize: 12, color: "#C62828" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {lookupButtons()}
            <button type="button" style={BTN_LIGHT} disabled={looking} onClick={openAddBand}>
              Add band manually
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
